"use client";

// Browser notifications from the visitor's side (lib/push.ts has the server's): asking the browser, the
// service worker (public/sw.js), and telling the server what this browser follows, again whenever that
// changes, so new videos and live favourites reach it with the site closed.

export type PushState = "unsupported" | "blocked" | "off" | "on";

export interface PushInterests {
  follows: string[];
  cams: string[];
  lang: string;
}

export const pushSupported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;

async function subscription(): Promise<PushSubscription | null> {
  const reg = await navigator.serviceWorker.getRegistration("/");
  return (await reg?.pushManager.getSubscription()) ?? null;
}

/** Whether this browser gets notifications. */
export async function pushState(): Promise<PushState> {
  if (!pushSupported()) return "unsupported";
  if (Notification.permission === "denied") return "blocked";
  return (await subscription()) ? "on" : "off";
}

/** A base64url key as the browser takes it. */
function keyBytes(key: string): Uint8Array<ArrayBuffer> {
  const b64 = (key + "=".repeat((4 - (key.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function send(sub: PushSubscription, interests: PushInterests) {
  await fetch("/api/push", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ subscription: sub.toJSON(), ...interests }) });
}

/** Asks for notifications (a tap must lead here: browsers only ask then) and subscribes. */
export async function enablePush(interests: PushInterests): Promise<PushState> {
  if (!pushSupported()) return "unsupported";
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "blocked" : "off";
  const { key } = (await (await fetch("/api/push")).json()) as { key: string | null };
  if (!key) return "unsupported";
  const reg = await navigator.serviceWorker.register("/sw.js");
  await navigator.serviceWorker.ready;
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }));
  await send(sub, interests);
  return "on";
}

export async function disablePush(): Promise<PushState> {
  const sub = await subscription();
  if (sub) {
    await fetch("/api/push", { method: "DELETE", headers: { "content-type": "application/json" }, body: JSON.stringify({ endpoint: sub.endpoint }) }).catch(() => {});
    await sub.unsubscribe().catch(() => false);
  }
  return "off";
}

/** Tells the server what this browser follows now, when it gets notifications. */
export async function updatePush(interests: PushInterests) {
  if (!pushSupported() || Notification.permission !== "granted") return;
  const sub = await subscription();
  if (sub) await send(sub, interests).catch(() => {});
}
